import {
    ACCEPTED_FILE_FORMATS_FOR_LIABILITY_REINVESTMENT_TAXES,
    optimizationDefaultColDefs,
} from '@/constants/portfolioOptimization.constant'
import { Box, Typography } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'

import { useFileParser } from '@/hooks/useFileParser'
import { IRateFileRecord } from '@/interfaces/portfolioOptimization.interface'
import { portfolioOptimizationDefaultGridOptions } from '@/utils/grid-utils/colDefs'
import { GridOptions } from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import FileUploadBox from './FileUploadBox'

interface ReinvestmentRateGridProps {
  onChange: (value: any) => void
  initialData?: IRateFileRecord[]
}

const ReinvestmentRateGrid: React.FC<ReinvestmentRateGridProps> = ({
  onChange,
  initialData,
}) => {
  const [fileRecords, setFileRecords] = useState<IRateFileRecord[]>([])
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const gridRef = useRef<AgGridReact>(null)

  const gridOptions: GridOptions = {
    ...portfolioOptimizationDefaultGridOptions,
    sideBar: false,
    noRowsOverlayComponentParams: () => {
      return {
        noRowsMessage: 'No rate available. Please upload a file.',
      }
    },
    rowSelection: 'single' as const,
    columnDefs: [
      {
        ...optimizationDefaultColDefs,
        floatingFilter: false,
        headerName: 'Time (years)',
        field: 'years',
        filter: 'agNumberColumnFilter',
      },
      {
        ...optimizationDefaultColDefs,
        floatingFilter: false,
        headerName: 'Rates (%)',
        field: 'rate',
        filter: 'agNumberColumnFilter',
      },
    ],
  }
  const handleFileSelect = async (file: File | null) => {
    setSelectedFile(file)
  }

  const { data, error, isLoading, parse } = useFileParser({
    file: selectedFile,
  })

  useEffect(() => {
    if (selectedFile) {
      parse()
    }
  }, [selectedFile])

  useEffect(() => {
    onChange(fileRecords)
  }, [fileRecords])

  useEffect(() => {
    if (data.length > 0 && !error && !isLoading) {
      const transformedData = data.map((d) => ({
        years: d.column1,
        rate: d.column2,
      }))
      setFileRecords(transformedData)
    }
  }, [error, isLoading, data])

  useEffect(() => {
    if (initialData && initialData.length > 0 && fileRecords.length === 0) {
      console.log('ReinvestmentRateGrid - Initializing with template data:', initialData)
      setFileRecords(initialData)
    }
  }, [initialData, fileRecords])

  return (
    <Box flex={1}>
      <Box marginBottom={2}>
        <Typography variant='h5'>Optimization Reinvestment Rate</Typography>
        <Typography variant='body2'>
          Upload a File or manually enter the Optimization Reinvestment Rate
          Values in the table below.
        </Typography>
        <FileUploadBox
          id='reinvestment-rate-upload'
          onFileSelect={handleFileSelect}
          acceptedFormats={
            ACCEPTED_FILE_FORMATS_FOR_LIABILITY_REINVESTMENT_TAXES
          }
        />
      </Box>
      <Box className='ag-theme-quartz'>
        <AgGridReact
          ref={gridRef}
          rowData={fileRecords}
          gridOptions={gridOptions}
          data-cy='reinvestment-rate-ag-grid'
        ></AgGridReact>
      </Box>
    </Box>
  )
}

export default ReinvestmentRateGrid
